import Header from 'components/headers/bulletins'
import DotsLoading from 'components/loadings/dotsLoading'
import { SectionError } from 'components/states'
import posthog from 'posthog-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { getMessagingEvent } from 'services/messagingHistory'
import { ChannelChips, ChannelCountChips } from '../ChannelChips'
import {
    failedLine,
    jalaliDateTime,
    openedLine,
    recipientsLine,
} from '../format'

const statusOf = (error: unknown) =>
    (error as { response?: { status?: number } })?.response?.status ?? null

function EventSummary({
    event,
    sample,
}: {
    event: IMessagingEventResponse
    sample?: string
}) {
    return (
        <div className=" mb-4 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-card">
            <span className=" inline-block rounded-lg bg-[#E8F5EC] px-3 py-1 text-xs text-green-1">
                {event.title}
            </span>
            <p className=" mt-1.5 text-[11px] text-text-secondary">
                {jalaliDateTime(event.at)}
            </p>

            <div className=" mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                <span className=" text-text-secondary">
                    {recipientsLine(event.recipients)}
                </span>
                {event.opened > 0 && (
                    <span className=" font-bold text-green-1">
                        {openedLine(event.opened)}
                    </span>
                )}
                {event.failed > 0 && (
                    <span className=" rounded-full bg-warning-soft px-2 py-0.5 text-warning-on">
                        {failedLine(event.failed)}
                    </span>
                )}
            </div>

            <ChannelCountChips counts={event.reach} />

            {sample && (
                <>
                    <p className=" mt-3 text-[11px] text-text-secondary">
                        نمونهٔ متن ارسال‌شده
                    </p>
                    <p
                        dir="auto"
                        className=" mt-1 whitespace-pre-line rounded-card border border-dashed border-divider p-3 text-xs leading-relaxed text-text-body"
                    >
                        {sample}
                    </p>
                </>
            )}
        </div>
    )
}

function RecipientCard({ recipient }: { recipient: IMessagingEventRecipient }) {
    return (
        <div className=" mb-2 rounded-2xl border border-[#EFEFEF] bg-white p-4 shadow-card">
            <div className=" flex items-center justify-between gap-2">
                <p className=" truncate text-sm font-bold text-heading">
                    {recipient.name}
                </p>
                {recipient.opened && (
                    <span className=" shrink-0 text-[11px] font-bold text-green-1">
                        دیده شد
                    </span>
                )}
            </div>
            <ChannelChips channels={recipient.channels} />
        </div>
    )
}

/**
 * One event and everyone it reached.
 *
 * The header is this send as a whole — how many people, on what channels, how
 * much of it failed, how much of it was opened — and the list underneath is
 * who, one card each, with the channels that carried their copy.
 */
export default function AnnounceEventDetail() {
    const { id, eventId } = useParams()
    const [event, setEvent] = useState<IMessagingEventResponse | null>(null)
    const [items, setItems] = useState<IMessagingEventRecipient[]>([])
    const [page, setPage] = useState(1)
    const [hasNext, setHasNext] = useState(false)
    const [loading, setLoading] = useState(true)
    const [loadingMore, setLoadingMore] = useState(false)
    const [failed, setFailed] = useState(false)
    const reported = useRef(false)

    const loadPage = useCallback(
        async (pageNumber: number, reset: boolean) => {
            if (!id || !eventId) return
            reset ? setLoading(true) : setLoadingMore(true)
            if (reset) setFailed(false)
            try {
                const data = await getMessagingEvent(id, eventId, pageNumber)
                const batch = data?.results ?? []
                // keep the header we already have, so paging never blanks it
                if (data) setEvent(data)
                setItems((prev) => (reset ? batch : [...prev, ...batch]))
                setHasNext(!!data?.has_next)
                if (reset && data && !reported.current) {
                    reported.current = true
                    posthog.capture('announce_event_viewed', {
                        moneypool_id: Number(id),
                        event_id: String(eventId),
                        kind: data.kind,
                        recipients: data.recipients,
                        opened: data.opened,
                        failed: data.failed,
                    })
                }
            } catch (error) {
                if (reset) setItems([])
                setHasNext(false)
                setFailed(true)
                posthog.capture('announce_history_load_failed', {
                    screen: 'event',
                    moneypool_id: Number(id),
                    event_id: String(eventId),
                    page: pageNumber,
                    status: statusOf(error),
                })
            } finally {
                setLoading(false)
                setLoadingMore(false)
            }
        },
        [id, eventId]
    )

    useEffect(() => {
        loadPage(1, true)
    }, [loadPage])

    const onLoadMore = () => {
        const next = page + 1
        setPage(next)
        loadPage(next, false)
    }

    return (
        <div className=" min-h-screen bg-canvas pb-10">
            <Header title="جزئیات پیام" />
            <div className=" px-3 pt-3">
                {loading ? (
                    <DotsLoading />
                ) : failed ? (
                    <SectionError
                        message="جزئیات این پیام دریافت نشد"
                        onRetry={() => loadPage(1, true)}
                    />
                ) : (
                    <>
                        {event && (
                            <EventSummary
                                event={event}
                                sample={items[0]?.text}
                            />
                        )}
                        <p className=" mb-2 text-sm font-bold text-heading">
                            گیرندگان
                        </p>
                        {items.length === 0 ? (
                            <div className=" rounded-2xl border border-[#EFEFEF] bg-white px-4 py-10 text-center text-sm text-text-secondary">
                                گیرنده‌ای برای این پیام ثبت نشده است
                            </div>
                        ) : (
                            <>
                                {items.map((recipient, index) => (
                                    <RecipientCard
                                        key={`${recipient.member_id}-${index}`}
                                        recipient={recipient}
                                    />
                                ))}
                                {hasNext && (
                                    <button
                                        type="button"
                                        onClick={onLoadMore}
                                        disabled={loadingMore}
                                        className=" my-3 w-full rounded-xl border border-green-1 py-2.5 text-sm text-green-1 disabled:opacity-60"
                                    >
                                        {loadingMore
                                            ? 'در حال بارگذاری...'
                                            : 'موارد بیشتر'}
                                    </button>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
